<template>
  <div class="flex flex-col gap-1 p-2">
    <div class="flex gap-1 flex-wrap">
      <!-- Projekt-Auswahl -->
      <div class="flex flex-col gap-1 w-52">
        <span class="text-sm text-trader-text">Project auswählen</span>
        <TRSearchableSelect
            ref="projectSelect"
            v-model="projectId"
            :items="projekts"
            placeholder="Projekt suchen..."
            @change="onProjectChange"
        />
      </div>

      <!-- Magazin-Auswahl -->
      <div class="flex flex-col gap-1 w-52">
        <span class="text-sm text-trader-text">Magazine auswählen</span>
        <TRSearchableSelect
            ref="magazineSelect"
            v-model="magazineId"
            :items="magazines"
            placeholder="Magazine suchen..."
            @change="onMagazineChange"
            :disabled="!projectId || projectId === 0"
        />
      </div>

      <!-- Artikel-Auswahl -->
      <div class="flex flex-col gap-1 w-52">
        <span class="text-sm text-trader-text">Articlenummer auswählen</span>
        <TRSearchableSelect
            ref="productSelect"
            v-model="productId"
            :items="articles"
            placeholder="Artikelnummer suchen..."
            :disabled="!magazineId || magazineId === 0"
        />
      </div>

      <!-- Markt-Auswahl -->
      <div class="flex flex-col gap-1 w-52">
        <span class="text-sm text-trader-text">Markt auswählen (Optional)</span>
        <TRSearchableSelect
            ref="marketSelect"
            v-model="marketId"
            :items="markets"
            placeholder="Markt suchen..."
            @change="onMarketChange"
            :disabled="!magazineId || magazineId === 0"
        />
      </div>


    </div>

    <TRButton text="Artikel Hinzufügen" @click="articleLink" :disabled="!isFormValid" />
  </div>
</template>

<script setup lang="ts">
import TRSearchableSelect from "@/components/Base/v2/TRSearchableSelect.vue";
import {computed, nextTick, onMounted, ref} from "vue";
import {mutation, query} from "@/graphql";
import TRButton from "@/components/Base/v2/TRButton.vue";
import {eventBus} from "@/application/EventBus";
import SignalBus from "@/application/SignalBus";

// Refs für die Select-Komponenten
const projectSelect = ref(null);
const magazineSelect = ref(null);
const marketSelect = ref(null);
const productSelect = ref(null);

// Datenlisten
const projekts = ref([]);
const magazines = ref([]);
const markets = ref([]);
const articles = ref([]);

// Ausgewählte Werte
const projectId = ref(0);
const magazineId = ref(0);
const marketId = ref(0);
const productId = ref(0);
const pagePosition = ref(0);

// Status für die Validierung
const isLoadingMagazines = ref(false);
const isLoadingMarkets = ref(false);
const isLoadingArticles = ref(false);

// Validierung für den Submit-Button
const isFormValid = computed(() => {
  return projectId.value && magazineId.value && productId.value;
});

const props = defineProps({
  page: {
    type: Array,
    required: true
  },
  virtualId: {
    type: Number,
    required: true
  }
});

onMounted(async () => {
  await fetchProjects();
});

// Projekt-Änderung Handling
async function onProjectChange(newProjectId) {
  console.log("Projekt geändert:", newProjectId);

  // Zurücksetzen und Leeren der abhängigen Selects
  forceResetSelect('magazine');
  forceResetSelect('market');
  forceResetSelect('product');

  // Laden der neuen Magazine
  if (newProjectId && newProjectId !== 0) {
    await fetchMagazines(newProjectId);
  }
}

// Magazin-Änderung Handling
async function onMagazineChange(newMagazineId) {
  console.log("Magazin geändert:", newMagazineId);

  // Zurücksetzen und Leeren der abhängigen Selects
  forceResetSelect('market');
  forceResetSelect('product');

  // Laden der abhängigen Daten
  if (newMagazineId && newMagazineId !== 0) {
    await Promise.all([
      fetchMarkets(newMagazineId),
      fetchProducts(newMagazineId)
    ]);
  }
}

// Markt-Änderung Handling
async function onMarketChange(newMarketId) {
  console.log("Markt geändert:", newMarketId);

  // Zurücksetzen des Artikel-Selects
  forceResetSelect('product');

  // Neu laden der Produkte
  if (magazineId.value && magazineId.value !== 0) {
    await fetchProducts(magazineId.value, newMarketId);
  }
}

// Forciertes Zurücksetzen eines Selects
function forceResetSelect(selectType) {
  // Je nach Typ den richtigen Ref und Value zurücksetzen
  let selectRef = null;

  switch(selectType) {
    case 'magazine':
      magazineId.value = 0;
      magazines.value = [];
      selectRef = magazineSelect.value;
      break;
    case 'market':
      marketId.value = 0;
      markets.value = [];
      selectRef = marketSelect.value;
      break;
    case 'product':
      productId.value = 0;
      articles.value = [];
      selectRef = productSelect.value;
      break;
  }

  // Reset-Methode der Komponente aufrufen, falls verfügbar
  nextTick(() => {
    if (selectRef && typeof selectRef.reset === 'function') {
      selectRef.reset();
    }
  });
}

// Daten-Abrufe
async function fetchProjects() {
  const [data, err] = await query.getProjectsWithIdAndName();
  if (err) {
    console.error("Fehler beim Laden der Projekte:", err);
    return;
  }
  projekts.value = data || [];
}

async function fetchMagazines(projectId) {
  try {
    isLoadingMagazines.value = true;
    const [data, err] = await query.getMagazinesWithIdAndName(projectId);
    if (err) {
      console.error("Fehler beim Laden der Magazine:", err);
      return;
    }
    magazines.value = data || [];
  } finally {
    isLoadingMagazines.value = false;
  }
}

async function fetchMarkets(magazineId) {
  try {
    isLoadingMarkets.value = true;
    const [data, err] = await query.getMarketsWithIdAndName(magazineId);
    if (err) {
      console.error("Fehler beim Laden der Märkte:", err);
      return;
    }
    markets.value = data || [];
  } finally {
    isLoadingMarkets.value = false;
  }
}

async function fetchProducts(magazineId, marketId = 0, pagePosition = 0) {
  try {
    isLoadingArticles.value = true;
    const [data, err] = await query.getProductsWithIdAndName(magazineId, marketId, pagePosition);
    if (err) {
      console.error("Fehler beim Laden der Artikel:", err);
      return;
    }
    articles.value = data || [];
  } finally {
    isLoadingArticles.value = false;
  }
}

async function articleLink() {
  if (!isFormValid.value) {
    console.log("Formular ist nicht gültig");
    return;
  }

  try {
    const [data, err] = await mutation.articleLink(
        props.virtualId,
        props.page.pageId,
        magazineId.value,
        pagePosition.value || 0,
        marketId.value || 0,
        productId.value
    );

    if (err) {
      console.error("Fehler beim Hinzufügen des Artikels:", err);
      return;
    }

    // Dialog schließen und Seite neu laden
    eventBus.closeModal();
    SignalBus.emit("reload-page");
  } catch (e) {
    console.error("Unerwarteter Fehler:", e);
  }
}
</script>